import React, { useCallback, useRef } from 'react';
import { ACTIONTYPE } from '../AppStateProvider';
import { testICECandidatePairSelection } from '../../../utils/iceCandidatePairTesting';

// Safety timeout: 45s (30s connection + 15s buffer)
const ICE_TEST_SAFETY_TIMEOUT_MS = 45_000;

export default function useICETest(dispatch: React.Dispatch<ACTIONTYPE>) {
  const iceTestRef = useRef<boolean>(false);
  const cancelledRef = useRef<boolean>(false);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const startICETest = useCallback(async () => {
    // Don't start a new ICE test if one is already running
    if (iceTestRef.current) {
      return;
    }

    iceTestRef.current = true;
    cancelledRef.current = false;
    dispatch({ type: 'ice-test-started' });

    // Per-test safety timeout
    safetyTimeoutRef.current = setTimeout(() => {
      if (iceTestRef.current && !cancelledRef.current) {
        console.warn('ICE test safety timeout: auto-failing after', ICE_TEST_SAFETY_TIMEOUT_MS, 'ms');
        cancelledRef.current = true;
        iceTestRef.current = false;
        dispatch({ type: 'ice-test-failed', error: new Error('ICE test timed out') });
      }
    }, ICE_TEST_SAFETY_TIMEOUT_MS);

    try {
      const result = await testICECandidatePairSelection((progress) => {
        if (cancelledRef.current) return; // Stop updating progress after cancel
        dispatch({
          type: 'ice-test-progress',
          progress
        });
      });

      // Don't dispatch results if cancelled — the cancel handler already transitioned the UI
      if (!cancelledRef.current) {
        dispatch({ type: 'ice-test-completed', result });
      }
    } catch (error) {
      if (!cancelledRef.current) {
        dispatch({
          type: 'ice-test-failed',
          error: error instanceof Error ? error : new Error('ICE test failed')
        });
      }
    } finally {
      iceTestRef.current = false;
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    }
  }, [dispatch]);

  const cancelICETest = useCallback(() => {
    cancelledRef.current = true;
    iceTestRef.current = false;
    if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    // Immediately mark the test as finished so the UI transitions
    dispatch({ type: 'ice-test-failed', error: new Error('Test cancelled') });
  }, [dispatch]);

  return { startICETest, cancelICETest } as const;
}
